import { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { ReactComponent as CheckBoxIcon } from "../../../../assets/img/icon/check_box_black_24dp.svg";
import { ReactComponent as Description } from "../../../../assets/img/icon/description_black_24dp.svg";
import { ReactComponent as CheckBoxIconBlank } from "../../../../assets/img/icon/disabled_by_default_black_24dp.svg";
import { ReactComponent as RadioUnchecked } from "../../../../assets/img/icon/radio_button_unchecked_black_24dp.svg";
import { ReactComponent as RadioChecked } from "../../../../assets/img/icon/radio_button_checked_black_24dp.svg";
import { getDateFormat } from "../../../../helpers/helper";
import { Record, setRecordParam } from "../../../../models/Record";
import {
  deleteRecord,
  getRecord,
} from "../../../../services/httpService/recordRequest";
import { GlobalContext } from "../../../contexts/globalContext";
import AnimatedBlock from "../../../ui/atoms/AnimatedBlock/AnimatedBlock";
import Button from "../../../ui/atoms/Button/Button";
import CardDashboard from "../../../ui/atoms/CardDashboard/CardDashboard";
import ExpandData from "../../../ui/atoms/ExpandData/ExpandData";
import PageTitle from "../../../ui/atoms/PageTitle/PageTitle";

const LIMIT = 5;
const dateNow = getDateFormat(new Date());

const RecordList = () => {
  const isMounted = useRef(true);
  const [state, setState] = useState({
    page: 1,
    tpage: 0,
    count: 0,
    patientName: "",
    startDate: "",
    endDate: "",
  });
  const [selectedRecord, setSelectedRecord] = useState([]);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([1, 2, 3, 4, 5, 6]);
  const [sharedState, dispatch] = useContext(GlobalContext);
  const history = useHistory();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({ ...prevState, [name]: value }));
  };

  const selectToggler = (recordId) => {
    setSelectedRecord((prevState) => {
      let newState = prevState;
      if (!newState.includes(recordId)) {
        newState = [...newState, recordId];
      } else {
        newState = newState.filter((record) => record !== recordId);
      }
      return newState;
    });
  };

  const handleDeleteAction = () => {
    const reducedRecords = selectedRecord.reduce((prev, curr, idx) => {
      if (idx !== 0) return `${prev},${curr}`;
      return curr;
    }, "");
    dispatch("confirm", {
      show: true,
      message: `Lanjutkan hapus rekam medis yang terpilih?`,
      callback: () => handleDeleteRecord(reducedRecords),
    });
  };

  const handleGetRecord = async (data) => {
    try {
      setLoading(true);
      let offset = 0;
      let page = state.page;
      if (data === "next") {
        page += 1;
        if (isMounted.current)
          setRecords((prevState) => [...prevState, new Array(LIMIT).fill(0)]);
      } else if (data === "prev") {
        page -= 1;
      } else if (data === "refresh") {
        page = 1;
      }
      offset = (page - 1) * LIMIT;

      const param = await setRecordParam(
        LIMIT,
        page,
        "patient,createdAt,device,notes,mode",
        state.patientName,
        state.startDate,
        state.endDate
      );

      if (param === "Interval waktu salah") {
        dispatch("alert", {
          type: "warning",
          message: param,
          show: true,
        });
        return;
      }

      let { result, count } = await getRecord(param);
      result = result.map(
        (res) =>
          new Record(
            res._id,
            res.createdAt,
            res.device,
            res.mode,
            res.patient,
            res.notes
          )
      );

      const tpage = Math.ceil(count / LIMIT);
      if (page !== 1) result = [...records, ...result];

      if (!isMounted.current) return null;
      setRecords(result);
      setState((prevState) => ({ ...prevState, tpage, page, count }));

      setLoading(false);
    } catch (error) {
      if (!isMounted.current) return null;
      setLoading(false);
    }
  };

  const handleChangePage = (path) => {
    history.push(path);
  };

  const handleDeleteRecord = async (_id) => {
    try {
      dispatch("confirm", {
        show: false,
        message: null,
        callback: null,
      });
      dispatch("isLoading", true);
      await deleteRecord({ _id: _id });
      handleGetRecord("refresh");

      if (!isMounted.current) return null;

      dispatch("isLoading", false);
    } catch (error) {
      if (!isMounted.current) return null;
      dispatch("isLoading", false);
      dispatch("alert", {
        show: true,
        message: error.message,
        type: "warning",
      });
    }
  };

  useEffect(() => {
    handleGetRecord();
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <>
      <div className="mt-2 lg:mt-6">
        <PageTitle
          title="Halaman Rekam Medis"
          description="Dapatkan riwayat pengukuran dengan menggunakan perangkat respinos."
          icon={
            <Description className="w-10 sm:w-12 lg:w-16 h-10 sm:h-12 lg:h-16 fill-current text-white" />
          }
        />

        {/* SUBMENU */}
        <div className="mt-10 flex flex-col xl:flex-row justify-start xl:justify-between items-center">
          {/* LEFT SUBMENU */}
          <div className="w-full xl:w-2/3 flex flex-wrap">
            <div className="w-full flex space-x-2 items-center mr-2 mt-2">
              <div className="text-primary font-medium flex-shrink-0 hidden md:block">
                Periode :
              </div>
              <input
                className="w-1/3 md:w-40 border-b border-primary text-primary px-1 focus:outline-none"
                type="date"
                placeholder="yyyy-mm-dd"
                name="startDate"
                value={state.startDate}
                onChange={handleChange}
                max={dateNow}
              />
              <span>-</span>
              <input
                className="w-1/3 md:w-40 border-b border-primary text-primary px-1 focus:outline-none"
                type="date"
                name="endDate"
                placeholder="yyyy-mm-dd"
                value={state.endDate}
                onChange={handleChange}
                min={state.startDate}
                max={dateNow}
              />
              <Button
                className="w-1/4 md:w-24 flex-shrink-0 bg-primary rounded px-2 py-1 text-sm text-white focus:outline-none focus:ring-2"
                handleClick={() => handleGetRecord("refresh")}
              >
                Cari
              </Button>
            </div>
          </div>
          {/* RIGHT SUBMENU */}
          <div className="w-full xl:w-1/3 flex space-x-3 justify-start xl:justify-end mt-3 xl:mt-0"></div>
        </div>

        <div className="mt-12">
          <CardDashboard
            title="Rekam Medis"
            icon={<Description fill={"#fafafa"} className="w-5 h-5" />}
          >
            <div className="relative pt-8">
              {!loading &&
                (records.length !== 0 ? (
                  <div className="absolute top-2 left-0 right-0">
                    <div className="relative flex">
                      {selectedRecord.length !== 0 && (
                        <div className="flex space-x-2">
                          <div>Aksi : </div>
                          <button
                            className="px-1.5 rounded-md border bg-warning text-white focus:outline-none focus:ring-2 mr-2 text-sm"
                            onClick={handleDeleteAction}
                          >
                            Hapus
                          </button>
                        </div>
                      )}
                      <div className="absolute top-0 right-0 text-sm text-gray-800 text-opacity-50 text-sm">
                        <span className="hidden md:inline-block">
                          Menampilkan
                        </span>{" "}
                        {`${records.length} dari ${state.count} data`}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="pb-3">
                    <p className="absolute top-2">
                      Rekam medis tidak ditemukan.
                    </p>
                  </div>
                ))}
              <div className="overflow-x-auto">
                {records.length !== 0 && (
                  <>
                    <table className="w-full my-3">
                      <thead>
                        <tr className="bg-secondary text-white">
                          <th className="px-2 py-2 text-left border border-white w-6 bg-primary">
                            #
                          </th>
                          <th className="px-2 py-2 text-left border border-white">
                            Tanggal / Waktu
                          </th>
                          <th className="px-2 py-2 text-left border border-white">
                            Pasien
                          </th>
                          <th className="px-2 py-2 text-left border border-white">
                            Mode
                          </th>
                          <th className="px-2 py-2 text-left border border-white">
                            Perangkat
                          </th>
                          <th className="px-2 py-2 text-left border border-white">
                            Review
                          </th>
                          <th className="px-2 py-2 text-left border border-white">
                            Aksi
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {records.map((record, idx) => {
                          if (record._id === undefined)
                            return (
                              <tr className="" key={"dummy-record-" + idx}>
                                <td className="px-1 py-1.5">
                                  <AnimatedBlock className="h-6" />
                                </td>
                                <td className="px-1 py-1.5">
                                  <AnimatedBlock className="h-6" />
                                </td>
                                <td className="px-1 py-1.5">
                                  <AnimatedBlock className="h-6" />
                                </td>
                                <td className="px-1 py-1.5">
                                  <AnimatedBlock className="h-6" />
                                </td>
                                <td className="px-1 py-1.5">
                                  <AnimatedBlock className="h-6" />
                                </td>
                                <td className="px-1 py-1.5">
                                  <AnimatedBlock className="h-6" />
                                </td>
                                <td className="px-1 py-1.5">
                                  <AnimatedBlock className="h-6" />
                                </td>
                              </tr>
                            );
                          return (
                            <tr
                              className={idx % 2 === 0 ? "bg-gray-100" : ""}
                              key={"record-" + idx}
                            >
                              <td
                                className="px-2 py-1.5 border-t cursor-pointer"
                                onClick={() => selectToggler(record._id)}
                              >
                                {selectedRecord.includes(record._id) ? (
                                  <RadioChecked className="w-3 h-3 fill-current text-primary" />
                                ) : (
                                  <RadioUnchecked className="w-3 h-3 fill-current text-primary" />
                                )}
                              </td>
                              <td className="px-2 py-1.5 border-t">
                                {record.createdAt}
                              </td>
                              <td className="px-2 py-1.5 border-t">
                                {record.patient.name}
                              </td>
                              <td className="px-2 py-1.5 border-t">
                                {record.mode}
                              </td>
                              <td className="px-2 py-1.5 border-t">
                                {record.device}
                              </td>
                              <td className="px-2 py-1.5 border-t">
                                {record.isReviewed ? (
                                  <CheckBoxIcon className="w-5 h-5 fill-current text-primary" />
                                ) : (
                                  <CheckBoxIconBlank className="w-5 h-5 fill-current text-gray-400" />
                                )}
                              </td>
                              <td
                                className="px-2 border-t"
                                style={{
                                  tableLayout: "fixed",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {/* <button
                                  className="px-1.5 py-0.5 rounded-md border border-warning text-warning focus:outline-none focus:ring-2 mr-2"
                                  onClick={() => handleOpenConfirm(record)}
                                >
                                  Hapus
                                </button> */}
                                <button
                                  onClick={() =>
                                    handleChangePage(`/record/${record._id}`)
                                  }
                                  className="px-1.5 py-0.5 rounded-md border border-secondary text-secondary focus:outline-none focus:ring-2"
                                >
                                  Lihat
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    <ExpandData
                      page={state.page}
                      tpage={state.tpage}
                      loading={loading}
                      handleMoreData={() => handleGetRecord("next")}
                    />
                  </>
                )}
              </div>
            </div>
          </CardDashboard>
        </div>
      </div>
    </>
  );
};

export default RecordList;
